import { Check, Palette } from '@mui/icons-material';
import { Box, Popover, TextField, Tooltip, Typography } from '@mui/material';
import React, { useState } from 'react';

interface ColorPickerProps {
  value: string;
  onChange: (color: string) => void;
  label?: string;
  disabled?: boolean;
}

const AVATAR_COLORS = [
  { color: '#3B6FD9', name: 'Ocean Blue' },
  { color: '#6CA8FF', name: 'Sky Blue' },
  { color: '#2E5AAC', name: 'Navy Blue' },
  { color: '#8B5CF6', name: 'Purple' },
  { color: '#EC4899', name: 'Pink' },
  { color: '#F59E0B', name: 'Amber' },
  { color: '#10B981', name: 'Emerald' },
  { color: '#EF4444', name: 'Red' },
  { color: '#64748B', name: 'Slate' },
  { color: '#06B6D4', name: 'Cyan' },
  { color: '#F97316', name: 'Orange' },
  { color: '#84CC16', name: 'Lime' },
  { color: '#A855F7', name: 'Violet' },
  { color: '#14B8A6', name: 'Teal' },
  { color: '#F43F5E', name: 'Rose' },
  { color: '#8B5A3C', name: 'Brown' },
];

export const ColorPicker: React.FC<ColorPickerProps> = ({
  value,
  onChange,
  label = 'Avatar Color',
  disabled = false,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    if (!disabled) {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleColorSelect = (color: string) => {
    onChange(color);
  };

  const open = Boolean(anchorEl);
  const selectedColorName = AVATAR_COLORS.find((c) => c.color === value)?.name || 'Custom';

  return (
    <Box>
      <Typography
        variant="body2"
        sx={{
          mb: 1.5,
          fontWeight: 700,
          fontSize: '0.75rem',
          letterSpacing: '0.05em',
          textTransform: 'uppercase',
          color: 'text.secondary',
        }}
      >
        {label}
      </Typography>
      <Box
        onClick={handleClick}
        sx={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          px: 2.5,
          py: 2,
          borderRadius: 2,
          border: '2px solid',
          borderColor: open ? 'primary.main' : 'divider',
          backgroundColor: 'background.paper',
          cursor: disabled ? 'not-allowed' : 'pointer',
          transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
          boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
          '&:hover': !disabled
            ? {
                borderColor: 'primary.main',
                backgroundColor: 'rgba(59, 111, 217, 0.02)',
                boxShadow: '0 4px 12px rgba(59, 111, 217, 0.15)',
                transform: 'translateY(-1px)',
              }
            : {},
        }}
      >
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: 2,
            backgroundColor: value,
            border: '3px solid white',
            boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
            flexShrink: 0,
          }}
        />
        <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.9375rem', color: 'text.primary', mb: 0.25 }}>
            {selectedColorName}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
              fontSize: '0.75rem',
              textTransform: 'uppercase',
              fontWeight: 600,
              letterSpacing: '0.05em',
            }}
          >
            {value}
          </Typography>
        </Box>
        <Palette sx={{ color: 'text.secondary', fontSize: 24, opacity: 0.6 }} />
      </Box>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        slotProps={{
          paper: {
            sx: {
              mt: 1.5,
              borderRadius: 3,
              boxShadow: '0 24px 64px rgba(0, 0, 0, 0.16), 0 8px 16px rgba(0, 0, 0, 0.08)',
              maxWidth: 380,
              background: 'linear-gradient(to bottom, #ffffff 0%, #f9fbff 100%)',
              border: '1px solid',
              borderColor: 'rgba(59, 111, 217, 0.15)',
              overflow: 'hidden',
            },
          },
        }}
      >
        <Box sx={{ p: 3 }}>
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="h6"
              sx={{
                mb: 0.5,
                fontWeight: 700,
                fontSize: '1.125rem',
                background: 'linear-gradient(135deg, #3B6FD9 0%, #6CA8FF 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                letterSpacing: '-0.02em',
              }}
            >
              Choose Avatar Color
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.875rem', lineHeight: 1.5 }}>
              Select a color that represents you
            </Typography>
          </Box>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(4, 1fr)',
              gap: 2,
              mb: 3,
            }}
          >
            {AVATAR_COLORS.map(({ color, name }) => (
              <Tooltip
                key={color}
                title={name}
                placement="top"
                arrow
                slotProps={{
                  tooltip: {
                    sx: {
                      bgcolor: 'rgba(0, 0, 0, 0.9)',
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      px: 1.5,
                      py: 0.75,
                    },
                  },
                  arrow: {
                    sx: {
                      color: 'rgba(0, 0, 0, 0.9)',
                    },
                  },
                }}
              >
                <Box
                  onClick={() => handleColorSelect(color)}
                  sx={{
                    width: 60,
                    height: 60,
                    borderRadius: 2.5,
                    backgroundColor: color,
                    cursor: 'pointer',
                    border: '3px solid',
                    borderColor: color === value ? 'white' : 'transparent',
                    boxShadow:
                      color === value ? `0 0 0 2.5px ${color}, 0 6px 20px ${color}80` : '0 3px 10px rgba(0,0,0,0.1)',
                    transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    '&:hover': {
                      transform: 'scale(1.1) translateY(-2px)',
                      boxShadow: `0 12px 28px ${color}70`,
                      zIndex: 1,
                      borderColor: color === value ? 'white' : 'rgba(255, 255, 255, 0.5)',
                    },
                    '&:active': {
                      transform: 'scale(1.05) translateY(-1px)',
                    },
                  }}
                >
                  {color === value && (
                    <Check
                      sx={{
                        color: 'white',
                        fontSize: 32,
                        fontWeight: 700,
                        filter: 'drop-shadow(0 2px 6px rgba(0,0,0,0.5))',
                      }}
                    />
                  )}
                </Box>
              </Tooltip>
            ))}
          </Box>

          <Box
            sx={{
              pt: 3,
              borderTop: '2px solid',
              borderColor: 'rgba(59, 111, 217, 0.1)',
            }}
          >
            <Typography
              variant="body2"
              sx={{
                mb: 1.5,
                fontWeight: 700,
                color: 'text.primary',
                fontSize: '0.875rem',
                letterSpacing: '0.02em',
              }}
            >
              Or enter a custom hex code
            </Typography>
            <TextField
              fullWidth
              size="medium"
              label="Hex Code"
              value={value}
              onChange={(e) => {
                const val = e.target.value;
                if (val.match(/^#[0-9A-F]{0,6}$/i)) {
                  onChange(val);
                }
              }}
              placeholder="#3B6FD9"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2.5,
                  backgroundColor: 'background.paper',
                  fontSize: '0.9375rem',
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'primary.main',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderWidth: 2,
                    borderColor: 'primary.main',
                  },
                },
                '& .MuiInputLabel-root': {
                  fontSize: '0.9375rem',
                  fontWeight: 600,
                },
              }}
              InputProps={{
                startAdornment: (
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: 1.5,
                      backgroundColor: value,
                      border: '2px solid',
                      borderColor: 'divider',
                      mr: 1.5,
                      flexShrink: 0,
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                    }}
                  />
                ),
              }}
            />
          </Box>
        </Box>
      </Popover>
    </Box>
  );
};
